import type {
	ImportTransform,
	ImportTransformDescriptor,
	ImportTransformParamSpec,
	ImportTransformType,
} from '@today/types'

// Param values of a catalogue transform (TO-400 BR-14), as the editor reads and
// writes them. A scalar is `true`/`false` as a boolean, otherwise the text as
// typed; a lookup is one `from=to` per line.

export const parseScalar = (text: string): string | boolean =>
	text === 'true' ? true : text === 'false' ? false : text

/** `from=to` lines → the lookup table; a line without `=` is ignored. */
export const parseLookup = (text: string): Record<string, string | boolean> =>
	Object.fromEntries(
		text
			.split('\n')
			.filter((line) => line.includes('='))
			.map((line) => {
				const at = line.indexOf('=')
				return [line.slice(0, at), parseScalar(line.slice(at + 1))]
			}),
	)

export const formatLookup = (values: unknown): string =>
	values && typeof values === 'object'
		? Object.entries(values as Record<string, unknown>)
				.map(([from, to]) => `${from}=${String(to)}`)
				.join('\n')
		: ''

const initialValue = (spec: ImportTransformParamSpec): unknown => {
	switch (spec.kind) {
		case 'integer':
			return spec.nullable ? null : spec.min
		case 'number':
			return 1
		case 'enum':
			return spec.values[0]
		case 'lookup':
			return {}
		default:
			return ''
	}
}

/** A new transform of `type` with every param at its starting value. */
export const newTransform = (type: ImportTransformType, descriptor: ImportTransformDescriptor): ImportTransform => ({
	type,
	...Object.fromEntries(descriptor.params.map((spec) => [spec.name, initialValue(spec)])),
})
